import React, { useState } from "react";
import { Box, Button, ButtonGroup } from "@mui/material";
import { styled, Theme, alpha } from "@mui/material/styles";
import { useGetProjectsQuery } from "../../services/project.service";
import MonthlyCalendar from "./monthlyCalendar/MonthlyCalendar";
import WeeklyCalendar from "./weeklyCalendar/WeeklyCalendar";
import "./styles.css";
import { useTitle } from "../../hooks/useTitle";
import { CardBase } from "../../components/global";

const StyledButtonGroup = styled(ButtonGroup)(({ theme }) => ({
  position: "relative",
  borderRadius: 16,
  overflow: "hidden",
  background:
    theme.palette.mode === "dark"
      ? `linear-gradient(135deg, ${alpha("#090909", 0.85)} 0%, ${alpha(
          "#1b1b1f",
          0.9
        )} 45%, ${alpha("#25252b", 0.85)} 100%)`
      : `linear-gradient(135deg, ${alpha("#f9fff9", 0.9)} 0%, ${alpha(
          "#ecfdf1",
          0.9
        )} 55%, ${alpha("#dff8e6", 0.85)} 100%)`,
  border: `1px solid ${alpha(
    theme.palette.mode === "dark" ? "#ffffff" : theme.palette.primary.main,
    0.4
  )}`,
  boxShadow:
    theme.palette.mode === "dark"
      ? "0 16px 40px rgba(0, 0, 0, 0.55), inset 0 1px 0 rgba(255,255,255,0.08)"
      : "0 18px 45px rgba(18, 144, 60, 0.16), inset 0 1px 0 rgba(255,255,255,0.7)",
  backdropFilter: "blur(24px)",
  WebkitBackdropFilter: "blur(24px)",
  display: "flex",
  flexWrap: "wrap",
  gap: 0,
  "&::after": {
    content: '""',
    position: "absolute",
    inset: 0,
    borderRadius: 16,
    background: `linear-gradient(135deg, transparent 0%, ${alpha(
      "#ffffff",
      0.08
    )} 100%)`,
    pointerEvents: "none",
  },
  [theme.breakpoints.down("sm")]: {
    width: "100%",
  },
  "& .MuiButton-root": {
    flex: 1,
    minWidth: 0,
    borderRadius: 0,
    border: "none",
    textTransform: "none",
    fontWeight: 600,
    letterSpacing: "0.04em",
    fontSize: "0.95rem",
    padding: theme.spacing(1.1, 2.5),
    transition: "all 0.25s ease",
    color: theme.palette.text.secondary,
    background: alpha(
      theme.palette.mode === "dark" ? "#1f1f24" : "#ffffff",
      theme.palette.mode === "dark" ? 0.25 : 0.65
    ),
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.4)",
    [theme.breakpoints.down("sm")]: {
      flexBasis: "50%",
      fontSize: "0.9rem",
      padding: theme.spacing(1, 2),
    },
    "&:not(:last-of-type)": {
      borderRight: `1px solid ${alpha(
        theme.palette.mode === "dark" ? "#ffffff" : "#12903C",
        0.25
      )}`,
      [theme.breakpoints.down("sm")]: {
        borderRight: "none",
        borderBottom: `1px solid ${alpha(
          theme.palette.mode === "dark" ? "#ffffff" : "#12903C",
          0.25
        )}`,
      },
    },
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: `0 14px 24px ${alpha(theme.palette.primary.main, 0.35)}`,
      borderColor: alpha(theme.palette.primary.main, 0.6),
      color: theme.palette.primary.contrastText,
      background: `linear-gradient(135deg, ${alpha(
        theme.palette.primary.light,
        0.9
      )} 0%, ${theme.palette.primary.main} 60%, ${theme.palette.primary.dark} 100%)`,
    },
    "&.MuiButton-outlined": {
      background: theme.palette.mode === "dark"
        ? alpha("#ffffff", 0.08)
        : alpha("#12903C", 0.1),
      color:
        theme.palette.mode === "dark"
          ? alpha("#ffffff", 0.85)
          : theme.palette.primary.main,
      border: `1px solid ${alpha(theme.palette.primary.main, 0.45)}`,
    },
    "&.MuiButton-contained": {
      background: `linear-gradient(140deg, ${alpha(
        theme.palette.primary.light,
        0.95
      )} 0%, ${theme.palette.primary.main} 55%, ${theme.palette.primary.dark} 100%)`,
      color: theme.palette.primary.contrastText,
      border: `1px solid ${alpha(theme.palette.primary.main, 0.9)}`,
      boxShadow: `0 16px 32px ${alpha(theme.palette.primary.main, 0.5)}`,
      "&:hover": {
        background: `linear-gradient(140deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
      },
    },
  },
}));

const CalendarPage = () => {
  const [selectedOption, setSelectedOption] = useState("Week");
  const { data: dataProjects, isLoading: isLoadingDataProjects } =
    useGetProjectsQuery({
      pageSize: 1000,
    });
  const buttonOptions = ["Week", "Month"];

  useTitle("Work Calendar");

  return (
    <Box
      sx={{
        px: { xs: 0, sm: 0 },
        pb: { xs: 4, sm: 6 },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: { xs: 2, md: 3 },
          flexWrap: "wrap",
          gap: 2,
          alignSelf: "stretch",
        }}
      >
        <Box>
          <Box
            component="h2"
            sx={{
              fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2rem" },
              fontWeight: 700,
              color: "text.primary",
              mb: 0.5,
              letterSpacing: "-0.02em",
            }}
          >
            Work Calendar
          </Box>
          <Box
            component="p"
            sx={{
              fontSize: "0.95rem",
              color: "text.secondary",
              m: 0,
            }}
          >
            View and manage your work schedule
          </Box>
        </Box>
        <StyledButtonGroup
          variant="outlined"
          aria-label="calendar view selection"
        >
          {buttonOptions.map((buttonOption) => {
            return (
              <Button
                key={buttonOption}
                variant={
                  selectedOption === buttonOption ? "contained" : "outlined"
                }
                onClick={() => {
                  setSelectedOption(buttonOption);
                }}
              >
                {buttonOption}
              </Button>
            );
          })}
        </StyledButtonGroup>
      </Box>
      <CardBase
        useGlassCard={true}
        sx={{
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: (theme: Theme) =>
              theme.palette.mode === "dark"
                ? "0 12px 40px rgba(0, 0, 0, 0.5)"
                : "0 12px 40px rgba(0, 0, 0, 0.15)",
          },
        }}
      >
        {selectedOption === "Month" && (
          <MonthlyCalendar data={dataProjects?.data} />
        )}
        {selectedOption === "Week" && (
          <WeeklyCalendar data={dataProjects?.data} />
        )}
      </CardBase>
    </Box>
  );
};

export default CalendarPage;
